/**
 * Dutch language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const nl = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Producten',
    product: 'product',
    productsPlural: 'producten',
    partner: 'Partner',
    name: 'Naam',
    category: 'Categorie',
    yes: 'Ja',
    no: 'Nee',
    back: 'Terug',
    next: 'Volgende',
    skip: 'Overslaan',
    filter: 'Filter',
    filterMode: 'Filtermodus',
    and: 'EN',
    or: 'OF',
    clearAll: 'Alles wissen',
    removeAll: 'Alles verwijderen',
    browseProducts: 'Producten bekijken',
    noProductsFound: 'Geen producten gevonden.',
    noResultsFor: (term: string) => `Geen resultaten voor ‘${term}’.`,
    new: 'NIEUW',
    pdf: 'PDF',
    viewAll: 'Alles bekijken',
    offlineBanner: 'Je bent offline. Sommige functies zijn beperkt.',
    offlineTooltip: 'Niet beschikbaar in offline modus',
    offlineLanguageHint: 'Offline: de interfacetaal wisselt, productgegevens blijven in de eerder geladen taal totdat je weer online bent',
    pwaUpdateAvailable: 'Een nieuwe versie is beschikbaar. Wordt bijgewerkt...',
    pwaInstallTitle: 'App installeren',
    pwaInstallDescription: 'Voeg toe aan je startscherm voor snelle toegang.',
    pwaInstallButton: 'Installeren',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Startpagina',
    products: 'Producten',
    wizard: 'Wizard',
    favorites: 'Favorieten',
    compare: 'Vergelijken',
    cart: 'Documenten',
    searchPlaceholder: 'Producten zoeken...',
    toggleTheme: 'Thema wisselen',
    logout: 'Uitloggen',
    admin: 'Admin',
    toggleMenu: 'Menu wisselen',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Productdatabase',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'E-mailadres',
    password: 'Wachtwoord',
    showPassword: 'Wachtwoord tonen',
    hidePassword: 'Wachtwoord verbergen',
    signingIn: 'Bezig met aanmelden...',
    signIn: 'Aanmelden',
    forgotPassword: 'Wachtwoord vergeten?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Welkom terug${name ? `, ${name}` : ''}!`,
    whatToDo: 'Wat wilt u vandaag doen?',
    stats: {
      products: 'Producten',
      new: 'Nieuw',
      favorites: 'Favorieten',
      inCart: 'In document',
    },
    actions: {
      browseProducts: 'Producten bekijken',
      viewAllProducts: (count: number) => (count === 1 ? '1 product bekijken' : `Alle ${count} producten bekijken`),
      browseProductsCount: (count: number) => `${count} producten`,
      loading: 'Laden...',
      startWizard: 'Wizard starten',
      findMatching: 'Passende producten vinden',
      favorites: 'Favorieten',
      productsSaved: (count: number) => `${count} opgeslagen`,
      meetingCart: 'Vergaderdocument',
      productsInCart: (count: number) => `${count} in document`,
    },
    browseByIndication: 'Bladeren op indicatie',
    topIndications: 'Uw top-indicaties',
    topIndicationsEmpty: 'Begin met het bekijken van producten om hier uw meest bekeken indicaties te zien.',
    newProducts: 'Nieuwe producten',
    newProductsTooltip: 'Alle producten die in de afgelopen 12 maanden zijn toegevoegd, worden als nieuw beschouwd.',
    newStudies: 'Nieuwe studies',
    newStudiesTooltip: 'Producten met onlangs gepubliceerde klinische studies in de afgelopen 12 maanden.',
    newStudyBadge: 'Nieuwe studie',
    publishedStudies: (count: number) => `${count} gepubliceerde klinische studies`,
    favoritesTitle: 'Favorieten',
    noFavorites: 'Nog geen favorieten. Klik op het hartpictogram van een product om het hier op te slaan.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'product' : 'producten'}`,
    recentlyViewed: 'Onlangs bekeken',
    noRecentlyViewed: 'Nog geen producten bekeken. Begin met de',
    productCatalog: 'Productcatalogus',
    viewAll: 'Alles bekijken',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Relevantie',
      nameAsc: 'Naam (A-Z)',
      nameDesc: 'Naam (Z-A)',
      articleNumber: 'Artikelnr.',
      partner: 'Partner',
      indication: 'Indicatie',
    },
    viewTable: 'Tabel',
    viewCards: 'Kaarten',
    productsFound: (count: number) => `${count} producten gevonden`,
    productCount: (count: number, total: number) => `${count} van ${total} producten`,
    loading: 'Producten laden...',
    adjustFilters: 'Pas uw filters aan.',
    resetFilters: 'Filters resetten',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Zoeken op naam, artikelnummer, ingrediënt, indicatie...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'Artikelnr.',
    name: 'Naam',
    partner: 'Partner',
    indication: 'Indicatie',
    application: 'Toepassing',
    country: 'Land',
    dailyDose: 'Dagdosering',
    claims: 'Claims',
    actions: 'Acties',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Overzicht',
      productData: 'Productgegevens',
      moreDetails: 'Prijs',
    },
    notFound: 'Product niet gevonden',
    backToOverview: 'Terug naar overzicht',
    marketingClaims: 'Marketingclaims',
    indications: 'Indicaties',
    applications: 'Toepassingen',
    countries: 'Landen',
    description: 'Beschrijving',
    shortDescription: 'Korte beschrijving',
    website: 'Productwebsite',
    multimedia: 'Multimediawebsite',
    studies: 'Studies',
    origin: 'Herkomst',
    dosage: 'Dosering',
    appearance: 'Uiterlijk',
    category: 'Categorie',
    articleNumber: 'Artikelnummer',
    active: 'Actief',
    created: 'Aangemaakt',
    showDailyPrice: 'Dagprijs tonen',
    perDay: '/ dag',
    generatingPdf: 'PDF wordt gegenereerd...',
    sendViaEmail: 'Verzenden via e-mail',
    emailSubject: (name: string, companyName: string) => `${companyName} — Productinformatieblad: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Geachte klant,\n\nBijgaand vindt u het productinformatieblad voor ${name}.\n\nNeem gerust contact met mij op als u vragen heeft of aanvullende informatie nodig heeft.\n\nMet vriendelijke groet,\n${userName}\n${companyName}`,
    toastEmail: 'E-mail succesvol verzonden!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Toevoegen aan favorieten',
    removeFromFavorites: 'Verwijderen uit favorieten',
    favorited: 'Toegevoegd aan favorieten',
    favorite: 'Favoriet',
    addToCart: 'Toevoegen aan document',
    removeFromCart: 'Verwijderen uit document',
    removeFromAllCarts: 'Uit alle documenten verwijderen',
    inCart: 'In document',
    addToCartLabel: 'Toevoegen aan document',
    inCartLabel: 'In document',
    cartLabel: 'Documenten',
    addToComparison: 'Toevoegen aan vergelijking',
    removeFromComparison: 'Verwijderen uit vergelijking',
    inComparison: 'In vergelijking',
    compared: 'Vergeleken',
    compare: 'Vergelijken',
    manageFavorites: 'Favorieten beheren',
    addToFavoritesNoGroup: 'Toevoegen aan favorieten (geen groep)',
    favoritesLabel: 'Favorieten',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Favorieten',
    noFavorites: 'Geen favorieten',
    noFavoritesDescription: 'Markeer producten als favoriet om ze hier te zien.',
    deleteGroup: {
      title: 'Groep verwijderen',
      message: (name: string) => `Weet u zeker dat u "${name}" wilt verwijderen?`,
      moveLabel: 'Producten verplaatsen naar:',
      ungroupedOption: 'Niet in een groep (ongegroepeerd)',
      confirm: 'Groep verwijderen',
      cancel: 'Annuleren',
    },
    browseProducts: 'Producten bekijken',
    dragProductsHint: 'Sleep producten hierheen',
    notInGroup: 'Niet in een groep',
    notInGroupWithCount: (count: number) => `Niet in een groep (${count})`,
    dragCopyHint: '⌘/⇧ ingedrukt — loslaten om naar groep te kopiëren',
    dragMoveHint: 'Loslaten om te verplaatsen · houd ⌘/⇧ vast om te kopiëren',
    addGroupToDocument: 'Groep aan document toevoegen',
    deleteGroupTitle: 'Groep verwijderen',
    groupNamePlaceholder: 'Groepsnaam...',
    create: 'Aanmaken',
    cancel: 'Annuleren',
    newGroup: 'Nieuwe groep',
    inDocument: 'In document',
    alreadyInDocument: 'De producten staan al in het document.',
    addedToDocument: 'Producten toegevoegd aan het document.',
    removedFromDocument: 'Producten verwijderd uit het document.',
    addToDocument: 'Toevoegen',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'product' : 'producten'}`,
    openProductTitle: 'Openen',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Productvergelijking',
    loading: 'Producten laden...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'Geen producten om te vergelijken',
    noProductsDescription: 'Voeg producten toe om ze naast elkaar te vergelijken.',
    browseProducts: 'Producten bekijken',
    addProduct: 'Product toevoegen',
    removeFromComparison: 'Verwijderen uit vergelijking',
    removeAll: 'Alles verwijderen',
    showDifferencesOnly: 'Alleen verschillen tonen',
    showingDifferencesOnly: 'Alleen verschillen worden getoond',
    hideDailyPrice: 'Dagprijs verbergen',
    dailyPriceHidden: 'Dagprijs verborgen',
    hidePrice: 'Prijs verbergen',
    priceHidden: 'Prijs verborgen',
    allIdentical: 'Alle waarden zijn gelijk voor deze producten.',
    rows: {
      articleName: 'Artikelnaam',
      articleNumber: 'Artikelnummer',
      partner: 'Partner',
      manufacturer: 'Fabrikant',
      category: 'Categorie',
      clinicalStudies: 'Klinische studies',
      bioactiveIngredients: 'Bioactieve ingrediënten / Dosering',
      indications: 'Indicaties',
      applications: 'Toepassingen',
      countries: 'Landen',
      dosage: 'Dosering',
      dailyDosage: 'Dagelijkse dosering',
      appearance: 'Uiterlijk',
      origin: 'Herkomst',
      sourceOrigin: 'Bron / Herkomst',
      marketingClaims: 'Marketingclaims',
      dailyPrice: 'Dagprijs',
      price: 'Prijs',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Ja',
      ongoing: 'Lopend',
      approved: 'Goedgekeurd',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Vergaderdocument',
    subtitle: 'Producten voor uw klantgesprek',
    emptyTitle: 'Uw document is leeg',
    emptyDescription: 'Voeg producten toe vanuit de catalogus.',
    moveUp: 'Omhoog verplaatsen',
    moveDown: 'Omlaag verplaatsen',
    remove: 'Verwijderen',
    rename: 'Hernoemen',
    lockedNotice: (date: string) => `Dit document is op ${date} verzonden en kan niet meer worden gewijzigd.`,
    generatePdf: 'PDF genereren',
    generatingPdf: 'PDF wordt gegenereerd...',
    sendViaEmail: 'Verzenden via e-mail',
    createOpportunity: 'Opportunity aanmaken in SalesForce',
    clearCart: 'Document wissen',
    archiveDocument: 'Document archiveren',
    confirmArchive: (name: string) => `"${name}" archiveren? Het verschijnt niet meer in uw documentenlijst en is daarna alleen nog zichtbaar voor beheerders.`,
    deleteDocument: 'Document verwijderen',
    confirmClear: 'Weet u zeker dat u het document wilt wissen?',
    confirmDelete: (name: string) => `"${name}" verwijderen? Het document en alle bijbehorende producten worden verwijderd.`,
    toastPdf: 'PDF is gegenereerd en gedownload.',
    toastPdfError: 'PDF genereren mislukt.',
    toastEmail: 'E-mail succesvol verzonden!',
    showDocuments: 'Documenten tonen',
    newDocument: 'Nieuw document',
    searchDocuments: 'Documenten zoeken...',
    noDocumentsFound: 'Geen documenten gevonden.',
    items: 'items',
    comingSoon: 'Binnenkort beschikbaar',
    comingSoonDescription: 'De SalesForce-integratie is momenteel in ontwikkeling. Deze functie wordt beschikbaar in een toekomstige update.',
    productCount: (count: number) => `${count} ${count === 1 ? 'product' : 'producten'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Productselectie (${count} producten)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Geachte klant,\n\nHieronder vindt u de productselectie van ons recente gesprek:\n\n${productList}\n\nNeem gerust contact met mij op als u vragen heeft of aanvullende informatie nodig heeft over een van deze producten.\n\nMet vriendelijke groet,\n${userName}\n${companyName}`,
    email: {
      title: 'Verzenden via e-mail',
      recipients: 'Ontvangers',
      subject: 'Onderwerp',
      message: 'Bericht',
      placeholder: 'Voer e-mailadres in...',
      cancel: 'Annuleren',
      send: 'E-mail verzenden',
      sending: 'Verzenden...',
      invalidEmail: 'Voer een geldig e-mailadres in.',
      duplicateEmail: 'Dit e-mailadres is al toegevoegd.',
      sendError: 'E-mail kon niet worden verzonden. Probeer het opnieuw.',
      products: 'Producten',
      productsCount: (selected: number, total: number) => `${selected} van ${total}`,
      selectAll: 'Alles selecteren',
      deselectAll: 'Alles deselecteren',
      noProductsWarning: 'Selecteer minimaal één product om in de e-mail op te nemen.',
      fileTypesLegend: 'Documenten toevoegen',
      fileTypesLoading: 'Beschikbare documenten laden…',
      fileTypes: {
        study: 'Studies',
        marketing: 'Marketingdocumenten',
        quality: 'Kwaliteitsdocumenten',
      },
      qualityHint: 'Kwaliteitsdocumenten worden niet bijgevoegd. {email} stuurt ze apart naar de ontvangers.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Instellingen',
    subtitle: 'Configureer uw applicatievoorkeuren',
    multiCart: {
      title: 'Meerdere documenten',
      description:
        'Schakel meerdere vergaderdocumenten in om producten voor verschillende klanten of vergaderingen te organiseren. U kunt maximaal 3 documenten hebben en er vanuit de header tussen wisselen.',
    },
    favoritesOnHome: {
      title: 'Favorieten op dashboard',
      description: 'Toon uw favorietensectie op het startscherm voor snelle toegang tot opgeslagen producten.',
    },
    favoriteGroups: {
      title: 'Favorietengroepen',
      description: 'Organiseer uw favorieten in groepen. U kunt maximaal 5 groepen maken en producten tussen ze slepen. Elke groep kan met één klik aan een document worden toegevoegd.',
    },
    productData: {
      title: 'Productgegevens',
      description: 'Toont wanneer de productgegevens voor het laatst van de server zijn geladen.',
      lastLoaded: 'Laatst geladen',
      never: 'Nog nooit geladen',
      reload: 'Producten opnieuw laden',
      reloading: 'Opnieuw laden...',
      reloadSuccess: 'Productgegevens opnieuw geladen',
      reloadError: 'Productgegevens konden niet opnieuw worden geladen',
    },
    changePassword: {
      title: 'Wachtwoord wijzigen',
      description: 'Werk het wachtwoord bij waarmee u inlogt.',
      currentPassword: 'Huidig wachtwoord',
      newPassword: 'Nieuw wachtwoord',
      confirmPassword: 'Nieuw wachtwoord bevestigen',
      submit: 'Wachtwoord bijwerken',
      submitting: 'Bijwerken...',
      success: 'Uw wachtwoord is bijgewerkt.',
      passwordMismatch: 'Wachtwoorden komen niet overeen',
      error: 'Kan wachtwoord niet bijwerken',
      backToSettings: 'Terug naar instellingen',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Land',
      application: 'Toepassing',
      indication: 'Indicatie',
    },
    selectCountry: 'Land selecteren',
    selectCountrySubtitle: 'In welke landen moet het product beschikbaar zijn?',
    selectApplication: 'Toepassing selecteren',
    selectApplicationSubtitle: 'Welke toedieningsvorm heeft uw voorkeur?',
    selectIndication: 'Indicatie selecteren',
    selectIndicationSubtitle: 'Welke gezondheidsgebieden zijn relevant?',
    showResults: 'Resultaten tonen',
    noSelection: 'Geen selectie gemaakt',
    selected: (count: number) => `${count} geselecteerd`,
    filterAnd: 'En',
    filterOr: 'Of',
    filterAndExplanation: 'Producten moeten aan alle geselecteerde criteria voldoen',
    filterOrExplanation: 'Producten moeten aan ten minste één criterium per stap voldoen',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filter',
    category: 'Categorie',
    country: 'Land',
    application: 'Toepassing',
    indication: 'Indicatie',
    partner: 'Partner',
    marketingClaims: 'Marketingclaims',
    agreement: 'Overeenkomst',
    agreements: {
      DA: 'DA — Distributieovereenkomst',
      CPA: 'CPA — Klantbescherming',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Intentieverklaring',
      none: 'Geen overeenkomst',
    },
    agreementDescriptions: {
      DA: 'Distributieovereenkomst (exclusief)',
      CPA: 'Klantbeschermingsovereenkomst (niet-exclusief)',
      Gentleman: 'Gentleman Agreement (mondeling, zonder contract)',
      LOI: 'Intentieverklaring (voorstadium van DA)',
      none: 'Nog geen overeenkomst voor dit land',
    },
    newProducts: 'Nieuwe producten',
    newStudy: 'Nieuw onderzoek',
    restrictedTo: (territories: string) => `Je weergave is beperkt tot ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Pagina niet gevonden',
    description: 'De pagina die u zoekt bestaat niet of is verplaatst.',
    goBack: 'Terug gaan',
    home: 'Startpagina',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Wachtwoord opnieuw instellen',
    email: 'E-mailadres',
    emailPlaceholder: 'uw@email.com',
    submit: 'Resetlink verzenden',
    submitting: 'Verzenden...',
    success: 'We hebben een wachtwoordresetlink naar uw e-mailadres gestuurd.',
    backToLogin: 'Terug naar aanmelden',
    error: 'Resetlink kon niet worden verzonden',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Nieuw wachtwoord instellen',
    email: 'E-mailadres',
    newPassword: 'Nieuw wachtwoord',
    confirmPassword: 'Wachtwoord bevestigen',
    submit: 'Wachtwoord opnieuw instellen',
    submitting: 'Bezig met opnieuw instellen...',
    passwordMismatch: 'Wachtwoorden komen niet overeen',
    backToLogin: 'Terug naar aanmelden',
    error: 'Wachtwoord kon niet worden gereset',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Bevat geen ingrediënten of bijproducten van dierlijke oorsprong.',
    Organic: 'Gecertificeerd biologisch — geteeld zonder synthetische pesticiden of meststoffen.',
    'Non-GMO': 'Vrij van genetisch gemodificeerde organismen.',
    'Clinically Studied': 'Werkzaamheid ondersteund door gepubliceerde klinische studies.',
    Patented: 'Beschermd door een of meer patenten voor een unieke samenstelling of proces.',
    Halal: 'Gecertificeerd Halal — conform islamitische voedingsrichtlijnen.',
    Kosher: 'Gecertificeerd Kosjer — conform joodse voedingswetten.',
    'Gluten-Free':
      'Bevat geen gluten — veilig voor mensen met coeliakie of glutengevoeligheid.',
  },
  pdf: {
    productDataSheet: 'Productgegevensblad',
    productDataSheets: 'Productgegevensbladen',
    keyFacts: 'Kerngegevens',
    dailyDosage: 'Dagelijkse dosering',
    appearance: 'Verschijningsvorm',
    bioactiveIngredient: 'Bioactief ingredient',
    indications: 'Indicaties',
    possibleApplications: 'Mogelijke toepassingen',
    description: 'Beschrijving',
    linkStudies: 'Studies',
    origin: 'Herkomst',
    confidential: 'Vertrouwelijk -- Alleen voor zakelijk gebruik',
    productCountOne: ':count product',
    productCountOther: ':count producten',
    clinicalStudies: 'Klinische studies',
    marketingClaims: 'Marketingclaims',
    territories: 'Gebieden',
    moq: 'Minimale bestelhoeveelheid in kg',
    shelfLife: 'Houdbaarheid',
    shelfLifeValue: ':months maanden',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NIEUW',
    badgeNewStudy: 'NIEUW ONDERZOEK',
    contactPerson: 'Uw contactpersoon bij Vivatis:',
    website: 'Productwebsite',
    multimedia: 'Multimediawebsite',
  },
} as const;

export default nl;
